import React, { useState } from 'react';
import { useAuth } from '../../auth/authContext';
import "./verificationpage.css";
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

interface LocationState {
    email: string;
}

export default function VerificationPage() {
    const { user, verifyUserEmail } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;
    const email = state?.email;
    const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleCodeChange = (index: number, value: string) => {
        if (value.length > 1) return;
        if (value && !/^\d$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        nextInput?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
        const prevInput = document.getElementById(`code-${index - 1}`);
        prevInput?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return;

        const newCode = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
        setCode(newCode);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const verificationCode = code.join('');

        if (verificationCode.length !== 6) {
            setError('Please enter the 6-digit code.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            console.log(`Verifying code: ${verificationCode} for email: ${email}`);
            await verifyUserEmail(email, verificationCode);
            nav("/", { replace: true });
        } catch (err: any) {
            setError(err.message || 'Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setLoading(true);
        setError('');

        try {
            // Replace this with your actual resend code API call
        } catch (err: any) {
            setError(err.message || 'Failed to resend code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="verification-container">
            <div className="verification-card">
                <h2>Verify Your Email</h2>
                <p className="verification-subtitle">
                We've sent a 6-digit code to <strong>{user?.email}</strong>
                </p>

                <form onSubmit={handleSubmit}>
                <div className="code-input-group" onPaste={handlePaste}>
                    {code.map((digit, index) => (
                    <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="code-input"
                        disabled={loading}
                        autoFocus={index === 0}
                    />
                    ))}
                </div>

                {error && <div className="error-message">{error}</div>}

                <button
                    type="submit"
                    className="verify-button"
                    disabled={loading || code.join('').length !== 6}
                >
                    {loading ? 'Verifying...' : 'Verify Email'}
                </button>
                </form>

                <div className="resend-section">
                <p>Didn't receive the code?</p>
                <button
                    type="button"
                    onClick={handleResendCode}
                    className="resend-button"
                    disabled={loading}
                >
                    Resend Code
                </button>
                </div>
            </div>
        </div>
    );
};